import { ReadonlyMat3, ReadonlyVec3 } from 'gl-matrix';
import { percentileWindow } from './percentileWindow';
import { windowedGreyscaleRgba } from './windowedGreyscale';

/**
 * Slices scattered per chunk by {@link computeAnteriorMipCancellable} before
 * yielding back to the browser. Sized for ~10 ms of work at the author's
 * measured ~0.6 ms/slice on a 512x512 CT, so a paint (the spinner, a slice
 * scroll) fits between chunks and a cancellation is honoured mid-volume.
 */
const CHUNK_SLICES = 16;

/**
 * A regular voxel volume in patient (DICOM LPS) space, expressed without any
 * engine or DOM type so this service stays framework-free. Callers build it
 * from `Image3D.floatPixelData` and `Image3D.grid`.
 */
export interface MipVolume {
    /**
     * Row-major voxel values; index is
     * `z * sizeVoxels[0] * sizeVoxels[1] + y * sizeVoxels[0] + x`.
     * Intensities are whatever the acquisition provides (HU, SUV-scale
     * activity, MR signal) — the projection is unit-agnostic and produces a
     * display-only greyscale, never a measurable value.
     */
    voxels: ArrayLike<number>;
    /** Voxel counts per grid axis, [x, y, z]. */
    sizeVoxels: readonly [number, number, number];
    /** Voxel spacing per grid axis, in mm. */
    spacingMm: readonly [number, number, number];
    /**
     * Column-major 3x3 orientation, as returned by `Grid.getOrientation()`:
     * column `i` is grid axis `i`'s unit direction in patient LPS space.
     */
    orientation: ReadonlyMat3;
}

/**
 * A greyscale anterior maximum-intensity projection, at the source volume's
 * own voxel resolution.
 *
 * The output is NOT resampled to isotropic pixels: one output pixel is one
 * voxel, so `widthPx`/`heightPx` are voxel counts and the pixels are square
 * only when the two in-plane spacings match. Consumers must size the drawn
 * image from {@link widthMm}/{@link heightMm}, not from the pixel counts, or
 * an anisotropic series renders squashed. Keeping the projection voxel-indexed
 * is what makes {@link mipPointToPx} exact rather than a resampling estimate.
 */
export interface AnteriorMipResult {
    /** RGBA pixels, row-major from the top-left, length `widthPx * heightPx * 4`. */
    rgba: Uint8ClampedArray;
    widthPx: number;
    heightPx: number;
    /** Physical extent of the projected plane, in mm. */
    widthMm: number;
    heightMm: number;
    /** Grid axis (0..2) mapped to output rows — the most superior-inferior one. */
    rowAxis: 0 | 1 | 2;
    /** Whether that axis runs bottom-to-top in the output (superior stays up). */
    rowFlipped: boolean;
    /** Grid axis (0..2) mapped to output columns — the most left-right one. */
    columnAxis: 0 | 1 | 2;
    /** Whether that axis runs right-to-left in the output (patient left stays
     *  on the viewer's right). */
    columnFlipped: boolean;
    /** Grid axis (0..2) collapsed by the projection — the most
     *  anterior-posterior one. */
    depthAxis: 0 | 1 | 2;
    /**
     * Flat voxel index shown by each pixel, or -1 where the ray has no finite
     * voxel or is drawn black (background, not a pick). Lowest index wins ties.
     */
    rayMaximumVoxelIndex: Int32Array;
}

/**
 * Index of the grid axis whose direction is most parallel to a patient axis,
 * ignoring sign. `orientation` is column-major, so grid axis `i`'s component
 * along patient axis `p` is element `3 * i + p`.
 */
function dominantGridAxis(
    orientation: ReadonlyMat3,
    patientAxis: 0 | 1 | 2
): 0 | 1 | 2 {
    let best: 0 | 1 | 2 = 0;
    let bestAbs = -1;
    for (const axis of [0, 1, 2] as const) {
        const component = Math.abs(orientation[3 * axis + patientAxis]);
        if (component > bestAbs) {
            bestAbs = component;
            best = axis;
        }
    }
    return best;
}

/**
 * Display window [center, width] from the projected values, by percentile.
 * The width is floored at 1 so a uniform projection (every voxel equal, e.g. a
 * synthetic phantom or an all-air sub-volume) yields a usable ramp instead of
 * dividing by zero.
 */
function autoWindow(projected: Float32Array): [number, number] {
    // Non-finite values are excluded before the percentiles are taken. A ray
    // whose voxels are all NaN never wins the max comparison and keeps its
    // -Infinity seed; letting that reach the percentiles would give an infinite
    // window width and a NaN centre, mapping EVERY pixel to black while the
    // panel still reported success — an all-black projection presented as a
    // valid one, which is the failure this panel's error state exists to
    // prevent. A projection with nothing finite in it throws instead, so the
    // caller shows its explicit unavailable state.
    const finite = projected.filter((value) => Number.isFinite(value));
    if (finite.length === 0) {
        throw new Error(
            'computeAnteriorMip: projection contains no finite values'
        );
    }
    // The percentile policy is shared with the reformat scout MIP; see
    // percentileWindow.
    return percentileWindow(finite);
}

/**
 * Project a volume to an anterior (front-facing) maximum-intensity view.
 *
 * The projection axis is the grid axis running most anterior-posterior, and
 * the output is the per-ray MAXIMUM along it — so the sign of that axis does
 * not matter and no depth ordering is needed. Rows run superior-at-top and
 * columns run patient-left-to-the-viewer's-right, the conventional anterior
 * presentation (you are facing the patient).
 *
 * This is a display aid: the greyscale mapping is an auto-derived percentile
 * window and carries no calibrated intensity. It is NOT a DRR — there is no
 * attenuation model — and it must not be used for measurement.
 *
 * Cost is one linear pass over `voxels`, which for a full CT volume is tens of
 * millions of reads. Callers are expected to compute it once and cache it, not
 * to recompute per frame.
 *
 * @throws if any dimension is zero, or if `voxels` does not match `sizeVoxels`.
 */
export function computeAnteriorMip(volume: MipVolume): AnteriorMipResult {
    const plan = planProjection(volume);
    scatterSlices(plan, volume, 0, volume.sizeVoxels[2]);
    return finalizeProjection(plan, volume);
}

/**
 * The cancellable variant of {@link computeAnteriorMip}: identical operations
 * in identical order (the result is deterministic and byte-equal to the
 * synchronous pass), but the z-loop runs in chunks of {@link CHUNK_SLICES}
 * with a macrotask yield between them, checking `isCancelled` before each.
 *
 * The yield is what lets the spinner actually paint, keeps slice scrolling
 * and window-level drags responsive during the multi-hundred-ms pass a large
 * series costs, and makes a mid-pass image switch abort instead of paying the
 * full stale computation. Returns null when cancelled; validation errors
 * still reject.
 */
export async function computeAnteriorMipCancellable(
    volume: MipVolume,
    isCancelled: () => boolean
): Promise<AnteriorMipResult | null> {
    const plan = planProjection(volume);
    const sizeZ = volume.sizeVoxels[2];
    for (let zStart = 0; zStart < sizeZ; zStart += CHUNK_SLICES) {
        // Yield FIRST: the initial chunk must not run before the caller's
        // spinner has had a frame to paint.
        await new Promise((resolve) => setTimeout(resolve, 0));
        if (isCancelled()) {
            return null;
        }
        scatterSlices(
            plan,
            volume,
            zStart,
            Math.min(zStart + CHUNK_SLICES, sizeZ)
        );
    }
    if (isCancelled()) {
        return null;
    }
    return finalizeProjection(plan, volume);
}

/** Everything the scatter pass needs, precomputed and validated once. */
interface ProjectionPlan {
    width: number;
    height: number;
    stepX: number;
    stepY: number;
    stepZ: number;
    originIndex: number;
    projected: Float32Array;
    argmax: Int32Array;
    rowAxis: 0 | 1 | 2;
    columnAxis: 0 | 1 | 2;
    rowFlipped: boolean;
    columnFlipped: boolean;
    depthAxis: 0 | 1 | 2;
}

function planProjection(volume: MipVolume): ProjectionPlan {
    const [sizeX, sizeY, sizeZ] = volume.sizeVoxels;
    if (sizeX <= 0 || sizeY <= 0 || sizeZ <= 0) {
        throw new Error(
            `computeAnteriorMip: empty volume (${sizeX}x${sizeY}x${sizeZ})`
        );
    }
    const voxelCount = sizeX * sizeY * sizeZ;
    if (volume.voxels.length !== voxelCount) {
        throw new Error(
            `computeAnteriorMip: expected ${voxelCount} voxels, got ` +
                `${volume.voxels.length}`
        );
    }

    // Patient LPS: +X left, +Y posterior, +Z superior.
    const projectionAxis = dominantGridAxis(volume.orientation, 1);
    const rowAxis = dominantGridAxis(volume.orientation, 2);
    if (projectionAxis === rowAxis) {
        throw new Error(
            'computeAnteriorMip: grid orientation maps the anterior and ' +
                'superior patient axes onto the same grid axis'
        );
    }
    const columnAxis = (3 - projectionAxis - rowAxis) as 0 | 1 | 2;

    // Superior at the top: flip when the row axis already increases superiorly.
    const rowFlipped = volume.orientation[3 * rowAxis + 2] > 0;
    // Patient left (+X) on the viewer's right: flip when the column axis
    // increases towards patient right.
    const columnFlipped = volume.orientation[3 * columnAxis] < 0;

    const width = volume.sizeVoxels[columnAxis];
    const height = volume.sizeVoxels[rowAxis];

    // Output-index step contributed by one voxel step along each grid axis.
    // The projection axis contributes nothing — that is what collapses it.
    const step: [number, number, number] = [0, 0, 0];
    step[rowAxis] = rowFlipped ? -width : width;
    step[columnAxis] = columnFlipped ? -1 : 1;
    step[projectionAxis] = 0;
    const originIndex =
        (rowFlipped ? (height - 1) * width : 0) +
        (columnFlipped ? width - 1 : 0);

    const projected = new Float32Array(width * height);
    projected.fill(-Infinity);
    const argmax = new Int32Array(width * height);
    argmax.fill(-1);

    const [stepX, stepY, stepZ] = step;
    return {
        width,
        height,
        stepX,
        stepY,
        stepZ,
        originIndex,
        projected,
        argmax,
        rowAxis,
        columnAxis,
        rowFlipped,
        columnFlipped,
        depthAxis: projectionAxis,
    };
}

/**
 * Scatter the slices in [zStart, zEnd) into the plan's projection buffer.
 *
 * Reads the volume in memory order (x fastest) and scatters into the output,
 * rather than gathering per output pixel: the gather order would stride
 * through the volume for every ray on a grid whose fastest axis is the one
 * being projected. Per-ray max is order-independent, so chunked and one-shot
 * passes produce identical buffers.
 */
function scatterSlices(
    plan: ProjectionPlan,
    volume: MipVolume,
    zStart: number,
    zEnd: number
): void {
    const [sizeX, sizeY] = volume.sizeVoxels;
    const { stepX, stepY, stepZ, originIndex, projected, argmax } = plan;
    const sliceStride = sizeX * sizeY;
    const voxels = volume.voxels;
    for (let z = zStart; z < zEnd; z++) {
        const zOut = originIndex + z * stepZ;
        const zIn = z * sliceStride;
        for (let y = 0; y < sizeY; y++) {
            const rowIn = zIn + y * sizeX;
            let out = zOut + y * stepY;
            for (let x = 0; x < sizeX; x++) {
                const value = voxels[rowIn + x];
                if (value > projected[out]) {
                    projected[out] = value;
                    argmax[out] = rowIn + x;
                }
                out += stepX;
            }
        }
    }
}

/**
 * Window the projection and pack it as greyscale RGBA, and drop the voxel
 * behind every pixel drawn at or below the window's lower end.
 */
function finalizeProjection(
    plan: ProjectionPlan,
    volume: MipVolume
): AnteriorMipResult {
    const {
        width,
        height,
        projected,
        argmax,
        rowAxis,
        columnAxis,
        rowFlipped,
        columnFlipped,
        depthAxis,
    } = plan;
    const [center, windowWidth] = autoWindow(projected);
    const lower = center - windowWidth / 2;
    const rgba = windowedGreyscaleRgba(
        projected,
        lower,
        center + windowWidth / 2
    );
    for (let pixel = 0; pixel < projected.length; pixel++) {
        if (projected[pixel] <= lower) {
            argmax[pixel] = -1;
        }
    }

    return {
        rgba,
        widthPx: width,
        heightPx: height,
        widthMm: width * volume.spacingMm[columnAxis],
        heightMm: height * volume.spacingMm[rowAxis],
        rowAxis,
        rowFlipped,
        columnAxis,
        columnFlipped,
        depthAxis,
        rayMaximumVoxelIndex: argmax,
    };
}

/**
 * Continuous overview pixel coordinates for a voxel-space position, as
 * [column, row] in [0, `widthPx`] × [0, `heightPx`].
 *
 * Takes the fractional voxel coordinate (as `Grid.toVoxelSpace` returns it,
 * with 0 at the corner of the first voxel) and applies the overview's axis
 * and flip mapping — the ONE place that mapping lives, so an indicator drawn
 * from it lands on the true position rather than snapping to a projected
 * pixel.
 */
export function mipPointToPx(
    mip: AnteriorMipResult,
    voxelPosition: ReadonlyVec3
): [number, number] {
    const col = voxelPosition[mip.columnAxis];
    const row = voxelPosition[mip.rowAxis];
    return [
        mip.columnFlipped ? mip.widthPx - col : col,
        mip.rowFlipped ? mip.heightPx - row : row,
    ];
}

/**
 * The flat voxel index behind the pixel at [column, row] (output pixels, as
 * {@link mipPointToPx} returns), or null outside it or where none is named.
 */
export function mipRayMaximumVoxelIndexAt(
    mip: AnteriorMipResult,
    positionPx: readonly [number, number]
): number | null {
    const column = Math.floor(positionPx[0]);
    const row = Math.floor(positionPx[1]);
    if (
        !(column >= 0 && column < mip.widthPx && row >= 0 && row < mip.heightPx)
    ) {
        return null;
    }
    const voxelIndex = mip.rayMaximumVoxelIndex[row * mip.widthPx + column];
    return voxelIndex < 0 ? null : voxelIndex;
}
